/**
 * Hierarchical Layout Engine
 * Implements the Sugiyama algorithm for layered graph drawing, optimized for workflow visualization
 *
 * The algorithm consists of four main phases:
 * 1. Layer Assignment - Assign nodes to discrete layers (usually based on longest path)
 * 2. Crossing Minimization - Reorder nodes within layers to minimize edge crossings
 * 3. Coordinate Assignment - Determine actual x,y positions for each node
 * 4. Edge Routing - Create edge paths that minimize crossings and improve readability
 */
import { logger } from '@/utils/logger';

import { getNodeCategory, isNodeOfCategory } from '../utils/node-type-utils';

class HierarchicalLayoutEngine {
  /**
   * Apply hierarchical layout to a set of nodes and edges
   * @param {Array} nodes - The nodes to arrange
   * @param {Array} edges - The edges connecting the nodes
   * @param {Object} options - Layout options
   * @returns {Object} Layout result with nodes and edges
   */
  static applyLayout(nodes, edges, options = {}) {
    const defaultOptions = {
      direction: 'LR', // LR = left-to-right, TB = top-to-bottom
      edgeRouting: 'default', // Changed from 'orthogonal' to 'default'
      edgeBundling: true, // Whether to bundle edges with similar paths
      rankSep: 1, // Number of ranks to keep between connected nodes
      alignConnected: true, // Try to align connected nodes
      considerNodeSizes: true, // Consider actual node sizes in layout
      respectActionFrames: true, // Consider action frames for layout
      actionFramePadding: 100, // Extra padding for action frames to prevent overlap
    };

    const layoutOptions = { ...defaultOptions, ...options };

    try {
      // Create a directed graph model
      const graph = this.createDigraph(nodes, edges);

      // Identify action frames and regular nodes using our new category helper
      const actionFrameNodes = nodes.filter((node) => node.type === 'actionFrame');
      const regularNodes = nodes.filter((node) => node.type !== 'actionFrame');

      // STEP 1: Layer Assignment

      let layeredNodes;

      if (layoutOptions.respectActionFrames && actionFrameNodes.length > 0) {
        // Special handling for action frames
        layeredNodes = this.assignLayersWithActionFrames(graph, layoutOptions, actionFrameNodes);
      } else {
        // Regular layer assignment
        layeredNodes = this.assignLayers(graph, layoutOptions);
      }

      // STEP 2: Crossing Minimization

      this.minimizeCrossings(layeredNodes, graph.edges, layoutOptions);

      // STEP 3: Coordinate Assignment

      const positionedNodes = this.assignCoordinates(layeredNodes, graph.edges, layoutOptions);

      // SPECIAL STEP: Adjust action frame positions to prevent overlap
      if (layoutOptions.respectActionFrames && actionFrameNodes.length > 0) {
        this.adjustActionFramePositions(positionedNodes, actionFrameNodes, layoutOptions);
      }

      // STEP 4: Edge Routing

      const routedEdges = this.routeEdges(positionedNodes, graph.edges, layoutOptions);

      // Ensure all original node properties are preserved
      const resultNodes = this.mergeNodeProperties(nodes, positionedNodes);

      return {
        nodes: resultNodes,
        edges: routedEdges,
      };
    } catch (error) {
      logger.error('Layout engine error:', error);
      return { nodes, edges }; // Return original if error occurs
    }
  }

  /**
   * Create a directed graph representation from nodes and edges
   * @param {Array} nodes - The nodes to include
   * @param {Array} edges - The edges connecting nodes
   * @returns {Object} Directed graph model
   */
  static createDigraph(nodes, edges) {
    // Create a mapping of node IDs to indices
    const nodeMap = new Map();
    nodes.forEach((node, index) => {
      nodeMap.set(node.id, index);
    });

    // Create adjacency lists for the graph
    const graph = {
      nodes: [...nodes],
      edges: [...edges],
      outEdges: Array(nodes.length)
        .fill(0)
        .map(() => []),
      inEdges: Array(nodes.length)
        .fill(0)
        .map(() => []),
    };

    // Populate adjacency lists
    edges.forEach((edge) => {
      const sourceIndex = nodeMap.get(edge.source);
      const targetIndex = nodeMap.get(edge.target);

      if (sourceIndex !== undefined && targetIndex !== undefined) {
        graph.outEdges[sourceIndex].push({
          target: targetIndex,
          id: edge.id,
        });
        graph.inEdges[targetIndex].push({
          source: sourceIndex,
          id: edge.id,
        });
      }
    });

    return graph;
  }

  /**
   * Assign layers to nodes with special handling for action frames
   * @param {Object} graph - The directed graph
   * @param {Object} options - Layout options
   * @param {Array} actionFrames - The action frame nodes
   * @returns {Array} Array of layers, each containing nodes
   */
  static assignLayersWithActionFrames(graph, options, actionFrames) {
    const { rankSep } = options;

    // First, identify which nodes are within action frames
    const nodeParentMap = new Map();

    // Map frame node IDs for quick lookup
    const frameIds = new Set(actionFrames.map((frame) => frame.id));

    // Match child nodes to their parent frames
    graph.nodes.forEach((node) => {
      if (node.parentNode && frameIds.has(node.parentNode)) {
        nodeParentMap.set(node.id, node.parentNode);
      }
    });

    // Identify nodes in action frames vs. regular nodes
    const frameNodes = new Set();
    nodeParentMap.forEach((_, nodeId) => frameNodes.add(nodeId));

    // First, identify all nodes with no incoming edges (sources)
    const sources = [];

    for (let i = 0; i < graph.nodes.length; i++) {
      // Skip internal frame nodes when identifying sources
      if (frameNodes.has(graph.nodes[i].id)) continue;

      if (graph.inEdges[i].length === 0) {
        sources.push(i);
      }
    }

    // If no sources found, find node with fewest in-edges as source
    if (sources.length === 0) {
      let minInEdges = Infinity;
      let sourceIndex = 0;

      for (let i = 0; i < graph.nodes.length; i++) {
        // Skip internal frame nodes when identifying sources
        if (frameNodes.has(graph.nodes[i].id)) continue;

        if (graph.inEdges[i].length < minInEdges) {
          minInEdges = graph.inEdges[i].length;
          sourceIndex = i;
        }
      }

      sources.push(sourceIndex);
    }

    // Topological sort
    const visited = new Set();
    const sorted = [];

    function visit(nodeIndex) {
      if (visited.has(nodeIndex)) return;
      visited.add(nodeIndex);

      graph.outEdges[nodeIndex]?.forEach((edge) => {
        visit(edge.target);
      });

      sorted.unshift(nodeIndex);
    }

    sources.forEach((source) => visit(source));

    // Calculate longest path to assign ranks
    const ranks = Array(graph.nodes.length).fill(-1);

    sources.forEach((source) => {
      ranks[source] = 0;
    });

    for (const nodeIndex of sorted) {
      const node = graph.nodes[nodeIndex];

      // For action frames, if they have explicit pathDepth, use that
      if (node.type === 'actionFrame' && node.pathDepth !== undefined) {
        ranks[nodeIndex] = Math.max(ranks[nodeIndex], node.pathDepth);
      } else if (node.data && node.data.pathDepth !== undefined) {
        // Use explicit pathDepth if available
        ranks[nodeIndex] = Math.max(ranks[nodeIndex], node.data.pathDepth);
      }

      graph.outEdges[nodeIndex]?.forEach((edge) => {
        // When assigning ranks to child nodes, preserve parent frame constraints
        const targetNode = graph.nodes[edge.target];
        const targetIsInFrame = frameNodes.has(targetNode.id);
        const targetFrameId = targetIsInFrame ? nodeParentMap.get(targetNode.id) : null;

        if (targetIsInFrame) {
          // Find the parent frame's rank
          const frameNode = graph.nodes.find((n) => n.id === targetFrameId);
          if (frameNode) {
            const frameIndex = graph.nodes.findIndex((n) => n.id === frameNode.id);
            if (frameIndex !== -1) {
              // Child node inherits its parent frame's rank
              // This ensures the child stays within its parent frame
              ranks[edge.target] = Math.max(ranks[edge.target], ranks[frameIndex]);
            } else {
              // Regular rank assignment if frame not found
              ranks[edge.target] = Math.max(ranks[edge.target], ranks[nodeIndex] + rankSep);
            }
          }
        } else {
          // Regular rank assignment for non-frame nodes
          ranks[edge.target] = Math.max(ranks[edge.target], ranks[nodeIndex] + rankSep);
        }
      });
    }

    // Handle action frames: preserve pathDepth and set rank
    actionFrames.forEach((frame) => {
      const frameIndex = graph.nodes.findIndex((n) => n.id === frame.id);
      if (frameIndex !== -1) {
        // Use frame's pathDepth or explicit rank if available
        const framePathDepth = frame.pathDepth || frame.data?.pathDepth;
        if (framePathDepth !== undefined) {
          ranks[frameIndex] = framePathDepth;
        }
      }
    });

    // Update ranks of children to match their parent frames
    graph.nodes.forEach((node, index) => {
      if (nodeParentMap.has(node.id)) {
        const parentId = nodeParentMap.get(node.id);
        const parentIndex = graph.nodes.findIndex((n) => n.id === parentId);

        if (parentIndex !== -1) {
          // Child inherits parent's rank/layer
          ranks[index] = ranks[parentIndex];
        }
      }
    });

    // Create layers
    const maxRank = Math.max(...ranks.filter((r) => r >= 0));
    const layers = Array(maxRank + 1)
      .fill(0)
      .map(() => []);

    for (let i = 0; i < graph.nodes.length; i++) {
      if (ranks[i] >= 0) {
        layers[ranks[i]].push({
          id: graph.nodes[i].id,
          index: i,
          rank: ranks[i],
          width: graph.nodes[i]._width || graph.nodes[i].width || 250,
          height: graph.nodes[i]._height || graph.nodes[i].height || 100,
          data: graph.nodes[i].data,
          originalIndex: i,
          position: { ...graph.nodes[i].position },
          isActionFrame: frameIds.has(graph.nodes[i].id),
          parentNode: graph.nodes[i].parentNode,
          type: graph.nodes[i].type,
        });
      }
    }

    // Handle any unassigned nodes (in case of cycles)
    for (let i = 0; i < graph.nodes.length; i++) {
      if (ranks[i] < 0) {
        layers[0].push({
          id: graph.nodes[i].id,
          index: i,
          rank: 0,
          width: graph.nodes[i]._width || graph.nodes[i].width || 250,
          height: graph.nodes[i]._height || graph.nodes[i].height || 100,
          data: graph.nodes[i].data,
          originalIndex: i,
          position: { ...graph.nodes[i].position },
          isActionFrame: frameIds.has(graph.nodes[i].id),
          parentNode: graph.nodes[i].parentNode,
          type: graph.nodes[i].type,
        });
      }
    }

    // Fill in indices within layers
    layers.forEach((layer) => {
      layer.forEach((node, idx) => {
        node.orderIndex = idx;
      });
    });

    return layers;
  }

  /**
   * Assign nodes to discrete layers using the longest path algorithm
   * @param {Object} graph - The directed graph
   * @param {Object} options - Layout options
   * @returns {Array} Array of layers, each containing nodes
   */
  static assignLayers(graph, options) {
    const { rankSep } = options;

    // First, identify all nodes with no incoming edges (sources)
    const sources = [];

    for (let i = 0; i < graph.nodes.length; i++) {
      if (graph.inEdges[i].length === 0) {
        sources.push(i);
      }
    }

    // If no sources found, find node with fewest in-edges as source
    if (sources.length === 0) {
      let minInEdges = Infinity;
      let sourceIndex = 0;

      for (let i = 0; i < graph.nodes.length; i++) {
        if (graph.inEdges[i].length < minInEdges) {
          minInEdges = graph.inEdges[i].length;
          sourceIndex = i;
        }
      }

      sources.push(sourceIndex);
    }

    // Topological sort
    const visited = new Set();
    const sorted = [];

    function visit(nodeIndex) {
      if (visited.has(nodeIndex)) return;
      visited.add(nodeIndex);

      graph.outEdges[nodeIndex]?.forEach((edge) => {
        visit(edge.target);
      });

      sorted.unshift(nodeIndex);
    }

    sources.forEach((source) => visit(source));

    // Calculate longest path to assign ranks
    const ranks = Array(graph.nodes.length).fill(-1);

    sources.forEach((source) => {
      ranks[source] = 0;
    });

    for (const nodeIndex of sorted) {
      const node = graph.nodes[nodeIndex];
      if (!node) continue;
      // Use pathDepth property if available
      if (node?.pathDepth !== undefined) {
        ranks[nodeIndex] = Math.max(ranks[nodeIndex], node.pathDepth);
      } else if (node.data && node.data.pathDepth !== undefined) {
        ranks[nodeIndex] = Math.max(ranks[nodeIndex], node.data.pathDepth);
      }

      graph.outEdges[nodeIndex]?.forEach((edge) => {
        ranks[edge.target] = Math.max(ranks[edge.target], ranks[nodeIndex] + rankSep);
      });
    }

    // Create layers
    const validRanks = ranks.filter((r) => r >= 0);

    // Handle edge case where no valid ranks exist
    if (validRanks.length === 0) {
      logger.warn('[HierarchicalLayoutEngine] No valid ranks found, defaulting to single layer');
      const layers = [[]];

      // Assign all nodes to layer 0
      for (let i = 0; i < graph.nodes.length; i++) {
        layers[0].push({
          id: graph.nodes[i].id,
          index: i,
          rank: 0,
          width: graph.nodes[i]._width || graph.nodes[i].width || 250,
          height: graph.nodes[i]._height || graph.nodes[i].height || 100,
          data: graph.nodes[i].data,
          originalIndex: i,
          position: { ...graph.nodes[i].position },
          type: graph.nodes[i].type,
        });
      }

      return layers;
    }

    const maxRank = Math.max(...validRanks);

    // Validate maxRank to prevent invalid array length
    if (!Number.isFinite(maxRank) || maxRank < 0 || maxRank > 10000) {
      logger.warn(
        '[HierarchicalLayoutEngine] Invalid maxRank value:',
        maxRank,
        'defaulting to single layer'
      );
      const layers = [[]];

      // Assign all nodes to layer 0
      for (let i = 0; i < graph.nodes.length; i++) {
        layers[0].push({
          id: graph.nodes[i].id,
          index: i,
          rank: 0,
          width: graph.nodes[i]._width || graph.nodes[i].width || 250,
          height: graph.nodes[i]._height || graph.nodes[i].height || 100,
          data: graph.nodes[i].data,
          originalIndex: i,
          position: { ...graph.nodes[i].position },
          type: graph.nodes[i].type,
        });
      }

      return layers;
    }

    const layers = Array(maxRank + 1)
      .fill(0)
      .map(() => []);

    for (let i = 0; i < graph.nodes.length; i++) {
      if (ranks[i] >= 0) {
        layers[ranks[i]].push({
          id: graph.nodes[i].id,
          index: i,
          rank: ranks[i],
          width: graph.nodes[i]._width || graph.nodes[i].width || 250,
          height: graph.nodes[i]._height || graph.nodes[i].height || 100,
          data: graph.nodes[i].data,
          originalIndex: i,
          position: { ...graph.nodes[i].position },
          type: graph.nodes[i].type,
        });
      }
    }

    // Handle any unassigned nodes (in case of cycles)
    for (let i = 0; i < graph.nodes.length; i++) {
      if (ranks[i] < 0) {
        layers[0].push({
          id: graph.nodes[i].id,
          index: i,
          rank: 0,
          width: graph.nodes[i]._width || graph.nodes[i].width || 250,
          height: graph.nodes[i]._height || graph.nodes[i].height || 100,
          data: graph.nodes[i].data,
          originalIndex: i,
          position: { ...graph.nodes[i].position },
          type: graph.nodes[i].type,
        });
      }
    }

    // Fill in indices within layers
    layers.forEach((layer) => {
      layer.forEach((node, idx) => {
        node.orderIndex = idx;
      });
    });

    return layers;
  }

  /**
   * Minimize edge crossings by reordering nodes within layers
   * @param {Array} layers - The assigned layers
   * @param {Array} edges - The graph edges
   * @param {Object} options - Layout options
   */
  static minimizeCrossings(layers, edges, options) {
    const MAX_ITERATIONS = 24;
    const nodeMap = new Map();

    // Create a lookup map for nodes by id
    layers.forEach((layer) => {
      layer.forEach((node) => {
        nodeMap.set(node.id, node);
      });
    });

    // Create adjacency structures for fast edge lookup
    const outgoing = new Map();
    const incoming = new Map();

    edges.forEach((edge) => {
      const sourceNode = nodeMap.get(edge.source);
      const targetNode = nodeMap.get(edge.target);

      if (sourceNode && targetNode) {
        if (!outgoing.has(sourceNode.id)) {
          outgoing.set(sourceNode.id, []);
        }
        outgoing.get(sourceNode.id).push(targetNode.id);

        if (!incoming.has(targetNode.id)) {
          incoming.set(targetNode.id, []);
        }
        incoming.get(targetNode.id).push(sourceNode.id);
      }
    });

    // Special handling for action frames - keep same-frame nodes together
    const nodeToFrameMap = new Map();

    // Build frame membership map
    for (const layer of layers) {
      for (const node of layer) {
        if (node.parentNode) {
          nodeToFrameMap.set(node.id, node.parentNode);
        } else if (node.isActionFrame || node.type === 'actionFrame') {
          // Keep track of action frame nodes
          nodeToFrameMap.set(node.id, node.id);
        }
      }
    }

    for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
      let improved = false;

      // Sweep down - adjust positions based on predecessors
      for (let i = 1; i < layers.length; i++) {
        improved =
          this.reorderLayer(
            layers[i],
            layers[i - 1],
            incoming,
            outgoing,
            'predecessors',
            nodeToFrameMap
          ) || improved;
      }

      // Sweep up - adjust positions based on successors
      for (let i = layers.length - 2; i >= 0; i--) {
        improved =
          this.reorderLayer(
            layers[i],
            layers[i + 1],
            incoming,
            outgoing,
            'successors',
            nodeToFrameMap
          ) || improved;
      }

      if (!improved) break;
    }

    // Final ordering of nodes in each layer
    layers.forEach((layer) => {
      layer.sort((a, b) => a.orderIndex - b.orderIndex);
    });
  }

  /**
   * Reorder nodes in a layer to minimize crossings
   * @param {Array} currentLayer - The layer to reorder
   * @param {Array} adjacentLayer - The adjacent layer to consider
   * @param {Map} incoming - Map of incoming edges
   * @param {Map} outgoing - Map of outgoing edges
   * @param {String} direction - 'predecessors' or 'successors'
   * @param {Map} nodeToFrameMap - Map of nodes to their parent frames
   * @returns {Boolean} True if the ordering improved
   */
  static reorderLayer(currentLayer, adjacentLayer, incoming, outgoing, direction, nodeToFrameMap) {
    const edgeMap = direction === 'predecessors' ? incoming : outgoing;

    // Calculate barycenter values for nodes in current layer
    const barycenters = new Map();

    for (const node of currentLayer) {
      const connectedNodes = edgeMap.get(node.id) || [];

      if (connectedNodes.length > 0) {
        let sum = 0;
        let count = 0;

        for (const connectedId of connectedNodes) {
          const connected = adjacentLayer.find((n) => n.id === connectedId);
          if (connected) {
            sum += connected.orderIndex;
            count++;
          }
        }

        barycenters.set(node.id, count > 0 ? sum / count : node.orderIndex);
      } else {
        // Nodes with no connections keep their position
        barycenters.set(node.id, node.orderIndex);
      }
    }

    // Group nodes by frame for consistent ordering
    const frameGroups = new Map();

    currentLayer.forEach((node) => {
      const frameId = nodeToFrameMap.get(node.id);

      if (frameId) {
        if (!frameGroups.has(frameId)) {
          frameGroups.set(frameId, []);
        }
        frameGroups.get(frameId).push(node);
      }
    });

    // Sort nodes by barycenter value
    const initialOrder = [...currentLayer].map((n) => n.id);

    // First sort all frame groups internally
    frameGroups.forEach((nodes, frameId) => {
      nodes.sort((a, b) => {
        return barycenters.get(a.id) - barycenters.get(b.id);
      });
    });

    // Now create a new layer order combining frame groups and individual nodes
    const newOrder = [];

    // Sort nodes based on their barycenter but keep frame nodes together
    currentLayer.sort((a, b) => {
      const aFrameId = nodeToFrameMap.get(a.id);
      const bFrameId = nodeToFrameMap.get(b.id);

      // If nodes belong to different frames, sort by frame barycenter
      if (aFrameId && bFrameId && aFrameId !== bFrameId) {
        // Calculate average barycenter for each frame
        const aNodes = frameGroups.get(aFrameId) || [];
        const bNodes = frameGroups.get(bFrameId) || [];

        const aAvg =
          aNodes.reduce((sum, n) => sum + barycenters.get(n.id), 0) / (aNodes.length || 1);
        const bAvg =
          bNodes.reduce((sum, n) => sum + barycenters.get(n.id), 0) / (bNodes.length || 1);

        return aAvg - bAvg;
      }

      // If only one node belongs to a frame, prioritize frames
      if (aFrameId && !bFrameId) return -1;
      if (!aFrameId && bFrameId) return 1;

      // If both nodes belong to the same frame or neither belongs to a frame
      // Sort by barycenter
      return barycenters.get(a.id) - barycenters.get(b.id);
    });

    // Update order indices
    currentLayer.forEach((node, idx) => {
      node.orderIndex = idx;
    });

    // Check if ordering improved
    const finalOrder = currentLayer.map((n) => n.id);
    return !this.areArraysEqual(initialOrder, finalOrder);
  }

  /**
   * Assign x and y coordinates to nodes based on their layer and order
   * @param {Array} layers - The ordered layers
   * @param {Array} edges - The graph edges
   * @param {Object} options - Layout options
   * @returns {Array} Nodes with positions assigned
   */
  static assignCoordinates(layers, edges, options) {
    const {
      direction,
      layerDistance,
      nodeDistance,
      considerNodeSizes,
      alignConnected,
      respectActionFrames = true,
    } = options;

    // Create a copy of all nodes with position information
    const positionedNodes = [];

    // Create a map to track node parent-child relationships
    const nodeParentMap = new Map();
    const frameNodes = new Set();
    const childToParentMap = new Map();

    // Identify action frames and their children
    layers.forEach((layer) => {
      layer.forEach((node) => {
        if (node.parentNode) {
          nodeParentMap.set(node.id, node.parentNode);
          childToParentMap.set(node.id, node.parentNode);
        }

        // Check if it's an action frame either by direct type or by using our category helper
        if (node.isActionFrame || node.type === 'actionFrame') {
          frameNodes.add(node.id);
        }
      });
    });

    // Assign initial X coordinates based on layer (rank)
    for (let i = 0; i < layers.length; i++) {
      const layer = layers[i];
      const layerX = i * layerDistance;

      // Calculate Y positions for nodes in this layer
      let currentY = 0;

      // First position parent frames in the layer
      const parentFramesInLayer = layer.filter(
        (node) => (node.isActionFrame || node.type === 'actionFrame') && !node.parentNode
      );

      for (let j = 0; j < parentFramesInLayer.length; j++) {
        const node = parentFramesInLayer[j];

        // Position the parent frame
        const positionedNode = {
          ...node,
          position: {
            x: layerX,
            y: currentY,
          },
          layer: i,
          order: j,
          // Track that this is a parent frame for special handling
          isParentFrame: true,
        };

        positionedNodes.push(positionedNode);

        // Update Y for next node, considering node height if enabled
        const nodeHeight = considerNodeSizes ? node._height || node.height || 120 : 120;
        currentY += nodeHeight + nodeDistance; // Add more space between parent frames
      }

      // Then position regular nodes (not within parent frames)
      const regularNodesInLayer = layer.filter(
        (node) => !node.parentNode && !frameNodes.has(node.id)
      );

      for (let j = 0; j < regularNodesInLayer.length; j++) {
        const node = regularNodesInLayer[j];

        // Create a positioned node
        const positionedNode = {
          ...node,
          position: {
            x: layerX,
            y: currentY,
          },
          layer: i,
          order: j + parentFramesInLayer.length,
        };

        positionedNodes.push(positionedNode);

        // Update Y for next node, considering node height if enabled
        const nodeHeight = considerNodeSizes ? node._height || node.height || 120 : 120;
        currentY += nodeHeight + nodeDistance; // Add more space between nodes vertically
      }
    }

    // Center layers vertically
    this.centerLayersVertically(positionedNodes, layers, options.nodeDistance);

    // Apply horizontal compaction if needed
    if (alignConnected) {
      this.alignConnectedNodes(positionedNodes, edges);
    }

    // Position child nodes within their parent frames
    if (respectActionFrames) {
      this.positionChildrenWithinParents(positionedNodes, childToParentMap, nodeDistance);
    }

    return positionedNodes;
  }

  /**
   * Position child nodes within their parent frames
   * @param {Array} nodes - All positioned nodes
   * @param {Map} childToParentMap - Map of child node IDs to parent frame IDs
   * @param {Number} nodeDistance - Distance between nodes
   */
  static positionChildrenWithinParents(nodes, childToParentMap, nodeDistance) {
    // Group children by parent
    const childrenByParent = new Map();

    // Find all child nodes
    nodes.forEach((node) => {
      const parentId = childToParentMap.get(node.id);
      if (parentId) {
        if (!childrenByParent.has(parentId)) {
          childrenByParent.set(parentId, []);
        }
        childrenByParent.get(parentId).push(node);
      }
    });

    // Position children within each parent
    childrenByParent.forEach((children, parentId) => {
      const parent = nodes.find((node) => node.id === parentId);
      if (!parent) {
        logger.warn(`Cannot find parent frame ${parentId} for positioning children`);
        return;
      }

      // Preserve the hierarchical relationships between nodes within the frame
      // First, find min/max layer values for children to determine how many layers we have
      let minLayer = Infinity;
      let maxLayer = -Infinity;

      children.forEach((child) => {
        minLayer = Math.min(minLayer, child.layer || 0);
        maxLayer = Math.max(maxLayer, child.layer || 0);
      });

      // Number of layers in this frame
      const layerCount = maxLayer - minLayer + 1;

      // Calculate horizontal spacing within the frame based on layer count
      const frameWidth = parent.style?.width || parent.width || 180;
      const horizontalSpacing = layerCount > 1 ? (frameWidth - 150) / (layerCount - 1) : 0;
      const leftPadding = 60; // Left padding for the parent frame

      // Organize children by their order within the same layer
      const childrenByLayer = new Map();

      children.forEach((child) => {
        // Normalize layer number to be relative to the frame (0-based)
        const relativeLayer = (child.layer || 0) - minLayer;

        if (!childrenByLayer.has(relativeLayer)) {
          childrenByLayer.set(relativeLayer, []);
        }
        childrenByLayer.get(relativeLayer).push(child);
      });

      // Position children within each layer inside the parent
      childrenByLayer.forEach((layerChildren, relativeLayer) => {
        // Sort by their original order
        layerChildren.sort((a, b) => a.order - b.order);

        // Calculate total height of this layer
        const layerHeight = layerChildren.reduce((total, child) => {
          return total + (child._height || child.height || 100) + nodeDistance / 2;
        }, 0);

        // Position vertically inside parent - center the layer within the parent
        const parentHeight = parent.style?.height || parent.height || 120;
        const padding = 60; // Top padding for parent frame header
        const startY = padding + (parentHeight - padding * 2 - layerHeight) / 2;
        let currentY = startY;

        // Calculate x position for this layer
        const layerX = leftPadding + relativeLayer * horizontalSpacing;

        layerChildren.forEach((child) => {
          // Position must be RELATIVE to parent's position
          const relativePosition = {
            x: layerX,
            y: currentY,
          };

          // Set the position
          child.position = relativePosition;

          // Ensure parent-child relationship is maintained
          child.parentNode = parentId;
          child.extent = 'parent';
          child.expandParent = true;

          // Move to next vertical position
          currentY += (child._height || child.height || 100) + nodeDistance / 2;
        });
      });
    });
  }

  /**
   * Center each layer vertically with increased spacing
   * @param {Array} nodes - The positioned nodes
   * @param {Array} layers - The layer structure
   * @param {Number} nodeDistance - Distance between nodes in a layer
   */
  static centerLayersVertically(nodes, layers, nodeDistance = 180) {
    // Group nodes by layer
    const nodesByLayer = {};

    for (const node of nodes) {
      if (!nodesByLayer[node.layer]) {
        nodesByLayer[node.layer] = [];
      }
      nodesByLayer[node.layer].push(node);
    }

    // Find the tallest layer
    let maxHeight = 0;

    for (const layer in nodesByLayer) {
      const layerNodes = nodesByLayer[layer];
      let layerHeight = 0;

      for (const node of layerNodes) {
        // Use larger height for action frames
        const nodeHeight =
          node.isParentFrame || node.type === 'actionFrame'
            ? node._height || node.height || 120
            : node._height || node.height || 120;
        layerHeight += nodeHeight;
      }

      // Add spacing between nodes using nodeDistance parameter
      layerHeight += (layerNodes.length - 1) * (nodeDistance * 0.5);
      maxHeight = Math.max(maxHeight, layerHeight);
    }

    // Center each layer vertically
    for (const layer in nodesByLayer) {
      const layerNodes = nodesByLayer[layer];
      let layerHeight = 0;

      for (const node of layerNodes) {
        const nodeHeight =
          node.isParentFrame || node.type === 'actionFrame'
            ? node._height || node.height || 120
            : node._height || node.height || 120;
        layerHeight += nodeHeight;
      }

      // Use nodeDistance here as well to maintain consistency
      layerHeight += (layerNodes.length - 1) * (nodeDistance * 0.5);

      // Calculate top offset to center the layer
      const topOffset = (maxHeight - layerHeight) / 2;

      // Update Y positions with nodeDistance-based spacing
      let currentY = topOffset;
      for (const node of layerNodes) {
        node.position.y = currentY;
        const nodeHeight =
          node.isParentFrame || node.type === 'actionFrame'
            ? node._height || node.height || 120
            : node._height || node.height || 120;
        currentY += nodeHeight + nodeDistance * 0.5;
      }
    }
  }

  /**
   * Align connected nodes to reduce edge crossings
   * @param {Array} nodes - The positioned nodes
   * @param {Array} edges - The graph edges
   */
  static alignConnectedNodes(nodes, edges) {
    // Create a map for fast node lookup
    const nodeMap = new Map();
    nodes.forEach((node) => {
      nodeMap.set(node.id, node);
    });

    // Create dependency graph between nodes in the same layer
    for (let i = 0; i < edges.length; i++) {
      const edge = edges[i];
      const sourceNode = nodeMap.get(edge.source);
      const targetNode = nodeMap.get(edge.target);

      if (!sourceNode || !targetNode) continue;

      // Try to position connected nodes at similar Y positions
      if (Math.abs(sourceNode.layer - targetNode.layer) === 1) {
        // Apply a gentle force to align Y coordinates
        const deltaY = targetNode.position.y - sourceNode.position.y;

        if (Math.abs(deltaY) > 50) {
          // Don't adjust positions of parent frames or their children
          if (
            !sourceNode.isParentFrame &&
            !targetNode.isParentFrame &&
            !sourceNode.parentNode &&
            !targetNode.parentNode
          ) {
            // Move source node slightly toward target
            sourceNode.position.y += deltaY * 0.1;

            // Move target node slightly toward source
            targetNode.position.y -= deltaY * 0.1;
          }
        }
      }
    }

    // Re-sort nodes by Y position within each layer to maintain consistent ordering
    const nodesByLayer = {};

    for (const node of nodes) {
      // Skip parent frames and their children for this operation
      if (node.isParentFrame || node.parentNode) continue;

      if (!nodesByLayer[node.layer]) {
        nodesByLayer[node.layer] = [];
      }
      nodesByLayer[node.layer].push(node);
    }

    for (const layer in nodesByLayer) {
      nodesByLayer[layer].sort((a, b) => a.position.y - b.position.y);

      // Ensure minimum spacing between nodes
      let currentY = nodesByLayer[layer][0]?.position.y || 0;

      for (const node of nodesByLayer[layer]) {
        if (node.position.y < currentY) {
          node.position.y = currentY;
        }

        currentY = node.position.y + (node._height || node.height || 100) + 100; // Increased from 60 to 100
      }
    }
  }

  /**
   * Route edges to minimize crossings and improve readability
   * @param {Array} nodes - The positioned nodes
   * @param {Array} edges - The edges to route
   * @param {Object} options - Layout options
   * @returns {Array} Edges with routing information
   */
  static routeEdges(nodes, edges, options) {
    const { edgeRouting, edgeBundling, respectActionFrames = true } = options;

    // Create a map for fast node lookup
    const nodeMap = new Map();
    nodes.forEach((node) => {
      nodeMap.set(node.id, node);
    });

    // Identify which nodes are in action frames for special edge routing
    const nodeToFrameMap = {};
    if (respectActionFrames) {
      nodes.forEach((node) => {
        if (node.parentNode) {
          nodeToFrameMap[node.id] = node.parentNode;
        }
      });
    }

    // Group edges by source-target layers for staggered routing
    const edgeGroups = {};

    edges.forEach((edge) => {
      const sourceNode = nodeMap.get(edge.source);
      const targetNode = nodeMap.get(edge.target);

      if (!sourceNode || !targetNode) return;

      // Create a key for this edge group (source layer to target layer)
      const groupKey = `${sourceNode.layer}-${targetNode.layer}`;

      if (!edgeGroups[groupKey]) {
        edgeGroups[groupKey] = [];
      }

      edgeGroups[groupKey].push({
        edge,
        sourceNode,
        targetNode,
      });
    });

    // Process each group with staggered offsets
    const routedEdges = [];

    Object.values(edgeGroups).forEach((group) => {
      // Sort edges within each group by source and target y-positions
      group.sort((a, b) => {
        // First sort by source y-position
        const aDiff = a.sourceNode.position.y - b.sourceNode.position.y;
        if (Math.abs(aDiff) > 10) return aDiff;

        // If sources are close together, sort by target y-position
        return a.targetNode.position.y - b.targetNode.position.y;
      });

      // Route each edge with an offset based on its position in the group
      group.forEach((item, index) => {
        const { edge, sourceNode, targetNode } = item;

        // Determine if this is an edge between nodes in different frames
        const sourceFrame = nodeToFrameMap[edge.source];
        const targetFrame = nodeToFrameMap[edge.target];

        // Determine edge type based on routing option
        let edgeType = 'default';

        switch (edgeRouting) {
          case 'curved':
            edgeType = 'default';
            break;
          case 'straight':
            edgeType = 'straight';
            break;
          default:
            // Use smoothstep for all cases
            edgeType = 'default';
        }

        // For edges between frames, always use smoothstep routing
        if (sourceFrame && targetFrame && sourceFrame !== targetFrame) {
          edgeType = 'default';
        }

        // Calculate source and target connection points
        const sourceX = sourceNode.position.x + (sourceNode._width || sourceNode.width || 250);
        const sourceY =
          sourceNode.position.y + (sourceNode._height || sourceNode.height || 100) / 2;

        const targetX = targetNode.position.x;
        const targetY =
          targetNode.position.y + (targetNode._height || targetNode.height || 100) / 2;

        // Define control points for the edge path with offset for staggering
        // This offset helps prevent edge overlaps
        const offset = (index - (group.length - 1) / 2) * 15;

        routedEdges.push({
          ...edge,
          type: edgeType,
          // sourceHandle: 'right',
          // targetHandle: 'left',
          data: {
            ...edge.data,
            routingIndex: index,
            routingGroupSize: group.length,
            sourceFrame,
            targetFrame,
          },
        });
      });
    });

    // Apply edge bundling if enabled and there are many edges
    if (edgeBundling && edges.length > 15) {
      return this.bundleEdges(routedEdges, nodes);
    }

    return routedEdges;
  }

  /**
   * Bundle edges that follow similar paths
   * @param {Array} edges - The edges to bundle
   * @param {Array} nodes - The positioned nodes
   * @returns {Array} Edges with bundling applied
   */
  static bundleEdges(edges, nodes) {
    // Create a map for node lookup
    const nodeMap = new Map();
    nodes.forEach((node) => {
      nodeMap.set(node.id, node);
    });

    // Group edges by source and target layers
    const edgeGroups = new Map();

    edges.forEach((edge) => {
      const sourceNode = nodeMap.get(edge.source);
      const targetNode = nodeMap.get(edge.target);

      if (!sourceNode || !targetNode) return;

      const layerKey = `${sourceNode.layer}-${targetNode.layer}`;

      if (!edgeGroups.has(layerKey)) {
        edgeGroups.set(layerKey, []);
      }

      edgeGroups.get(layerKey).push(edge);
    });

    // Process each group to identify bundles
    const bundledEdges = [];

    for (const [layerKey, groupEdges] of edgeGroups) {
      // Skip small groups
      if (groupEdges.length < 3) {
        bundledEdges.push(...groupEdges);
        continue;
      }

      // Sort edges by their source and target Y positions
      groupEdges.sort((a, b) => {
        const sourceA = nodeMap.get(a.source);
        const sourceB = nodeMap.get(b.source);
        const targetA = nodeMap.get(a.target);
        const targetB = nodeMap.get(b.target);

        if (!sourceA || !sourceB || !targetA || !targetB) return 0;

        // First compare source Y positions
        const sourceYDiff = sourceA.position.y - sourceB.position.y;
        if (Math.abs(sourceYDiff) > 15) return sourceYDiff;

        // If source positions are similar, compare target positions
        return targetA.position.y - targetB.position.y;
      });

      // Add all processed edges
      bundledEdges.push(...groupEdges);
    }

    return bundledEdges;
  }

  /**
   * Merge the positioned nodes with the original node properties
   * @param {Array} originalNodes - The original nodes
   * @param {Array} positionedNodes - The positioned nodes
   * @returns {Array} Merged nodes
   */
  static mergeNodeProperties(originalNodes, positionedNodes) {
    const nodeMap = new Map();
    positionedNodes.forEach((node) => {
      nodeMap.set(node.id, node);
    });

    return originalNodes.map((node) => {
      const positionedNode = nodeMap.get(node.id);

      if (positionedNode) {
        // Make sure to preserve parent-child relationships from positioned nodes
        // They may contain updated parent-child information
        const result = {
          ...node,
          position: positionedNode.position,
          // Preserve parent-child relationship if it exists in positioned node
          parentNode: positionedNode.parentNode || node.parentNode,
          extent: positionedNode.extent || node.extent,
          // Include additional layout information that might be useful
          layoutInfo: {
            layer: positionedNode.layer,
            order: positionedNode.order,
            isParentFrame: positionedNode.isParentFrame,
            nodeCategory: getNodeCategory(node.type),
          },
        };

        return result;
      }

      return node;
    });
  }

  /**
   * Helper function to check if two arrays are equal
   * @param {Array} arr1 - First array
   * @param {Array} arr2 - Second array
   * @returns {Boolean} True if arrays are equal
   */
  static areArraysEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;

    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) return false;
    }

    return true;
  }

  /**
   * Adjust action frame positions to prevent overlap
   * @param {Array} nodes - All nodes with positions assigned
   * @param {Array} actionFrameNodes - Original action frame nodes
   * @param {Object} options - Layout options
   */
  static adjustActionFramePositions(nodes, actionFrameNodes, options) {
    if (!actionFrameNodes || actionFrameNodes.length < 2) return;

    // Create map of node ID to actual node
    const nodeMap = new Map();
    nodes.forEach((node) => nodeMap.set(node.id, node));

    // Get positioned action frames
    const positionedFrames = actionFrameNodes
      .map((frame) => nodeMap.get(frame.id))
      .filter((frame) => frame && frame.position);
    if (positionedFrames.length < 2) return;

    // Sort frames by x position (left to right)
    positionedFrames.sort((a, b) => a.position.x - b.position.x);

    // First, organize frames by layers based on x position
    const frameLayers = [];
    let currentLayer = [];
    let currentLayerX = positionedFrames[0].position.x;
    const layerThreshold = options.layerDistance / 2;

    positionedFrames.forEach((frame) => {
      // If this frame is far enough from current layer, start a new layer
      if (Math.abs(frame.position.x - currentLayerX) > layerThreshold) {
        if (currentLayer.length > 0) {
          frameLayers.push(currentLayer);
        }
        currentLayer = [frame];
        currentLayerX = frame.position.x;
      } else {
        currentLayer.push(frame);
      }
    });

    // Add the last layer
    if (currentLayer.length > 0) {
      frameLayers.push(currentLayer);
    }

    // Process each layer to prevent vertical overlap
    frameLayers.forEach((layer) => {
      // Sort frames in this layer by y position (top to bottom)
      layer.sort((a, b) => a.position.y - b.position.y);

      // Adjust positions to prevent overlap
      for (let i = 1; i < layer.length; i++) {
        const prevFrame = layer[i - 1];
        const currentFrame = layer[i];

        // Calculate frame bottom edges with dimensions - use more conservative fallback values
        const prevHeight = prevFrame.style?.height || prevFrame._height || 120;
        const prevBottom = prevFrame.position.y + prevHeight;
        const currentTop = currentFrame.position.y;

        // Check if the frames overlap vertically
        const overlap = prevBottom - currentTop;

        // If there's overlap, push the current frame down
        if (overlap >= -options.actionFramePadding) {
          const adjustment = overlap + options.actionFramePadding;
          currentFrame.position.y += adjustment;

          // Also move all child nodes
          const frameChildNodes = nodes.filter(
            (node) => node.parentNode === currentFrame.id && node.id !== currentFrame.id
          );

          frameChildNodes.forEach((child) => {
            if (child.position) {
              child.position.y += adjustment;
            }
          });
        }
      }
    });

    // Now handle horizontal spacing between layers
    for (let i = 1; i < frameLayers.length; i++) {
      const prevLayer = frameLayers[i - 1];
      const currentLayer = frameLayers[i];

      // Find rightmost edge of previous layer
      let maxRight = -Infinity;
      prevLayer.forEach((frame) => {
        // Use more conservative fallback values here too
        const frameWidth = frame.style?.width || frame.width || 180;
        const right = frame.position.x + frameWidth;
        maxRight = Math.max(maxRight, right);
      });

      // Find leftmost edge of current layer
      let minLeft = Infinity;
      currentLayer.forEach((frame) => {
        minLeft = Math.min(minLeft, frame.position.x);
      });

      // Check if layers are too close
      const horizontalGap = minLeft - maxRight;
      const minRequiredGap = options.actionFramePadding;

      if (horizontalGap < minRequiredGap) {
        // Calculate adjustment needed
        const adjustment = minRequiredGap - horizontalGap;

        // Move all frames in current layer to the right
        currentLayer.forEach((frame) => {
          frame.position.x += adjustment;

          // Also move all child nodes
          const frameChildNodes = nodes.filter(
            (node) => node.parentNode === frame.id && node.id !== frame.id
          );

          frameChildNodes.forEach((child) => {
            if (child.position) {
              child.position.x += adjustment;
            }
          });
        });
      }
    }
  }
}

export default HierarchicalLayoutEngine;
